import type { Position } from '@/types/domain'

// ── Budget ────────────────────────────────────────────────────────────────────
export const BUDGET = 200

// ── Formations (DEF-MID-FWD; GK always 1) ────────────────────────────────────
// domain.ts Formation is incomplete — define all 8 valid formations here.
export const VALID_FORMATIONS = [
  '5-4-1',
  '5-3-2',
  '5-2-3',
  '4-5-1',
  '4-4-2',
  '4-3-3',
  '3-5-2',
  '3-4-3',
] as const
export type ValidFormation = (typeof VALID_FORMATIONS)[number]
export const VALID_FORMATIONS_SET: ReadonlySet<string> = new Set(VALID_FORMATIONS)

// ── Position limits ───────────────────────────────────────────────────────────
export const POSITION_MAX = {
  GK:  2,
  DEF: 5,
  MID: 5,
  FWD: 3,
} as const satisfies Record<Position, number>

export const POSITION_MIN_STARTERS = {
  GK:  1,
  DEF: 3,
  MID: 2,
  FWD: 1,
} as const satisfies Record<Position, number>

// ── Draft types ───────────────────────────────────────────────────────────────

export type DraftPlayer = {
  readonly playerId:     string
  readonly name:         string
  readonly displayName:  string
  readonly position:     Position
  readonly price:        number
  readonly teamCode:     string
  readonly teamName:     string
  readonly isEliminated: boolean
  readonly gwPoints:     number | null
}

export type SquadDraft = {
  starters: DraftPlayer[]       // up to 11; GK first, then DEF, MID, FWD
  benchGk:  DraftPlayer | null  // reserved GK bench slot
  bench:    DraftPlayer[]       // outfield bench, max 3; index 0 = Sub1 (highest priority)
  isDirty:  boolean
}

export const EMPTY_DRAFT: SquadDraft = {
  starters: [],
  benchGk:  null,
  bench:    [],
  isDirty:  false,
}

// ── Formation result ──────────────────────────────────────────────────────────

export type PositionCounts = {
  gk:  number
  def: number
  mid: number
  fwd: number
}

export type FormationResult =
  | { valid: true;  formation: ValidFormation }
  | { valid: false; counts: PositionCounts }

// ── API response shapes ───────────────────────────────────────────────────────

export type GwHistoryEntry = {
  gameweekNumber:        number
  points:                number
  opponent:              string | null
  result:                string | null
  // false = the team played this GW but the player did not appear (injury / suspension /
  // left out). The row shows all-zero stats and is excluded from the "rounds played" count.
  played:                boolean
  minutes:               number
  goals:                 number
  assists:               number
  cleanSheet:            boolean
  defensiveContribution: number
  saves:                 number
  goalsConceded:         number
  ownGoals:              number
  yellowCards:           number
  redCards:              number
  penaltiesSaved:        number
  successDribbles:       number
  penaltiesMissed:       number
  penaltiesWon:          number
  shotsOnTarget:         number
}

// Item from GET /v1/players
// Injury/suspension status synced from API-Football. Mirrors Player.InjuryStatus on the API.
export type InjuryStatus = 'NONE' | 'SUSPENDED' | 'INJURED_OUT' | 'INJURED_DOUBTFUL'

export type PlayerListItem = {
  playerId:     string
  name:         string
  displayName:  string
  position:     Position
  price:        number
  teamCode:     string
  teamName:     string
  teamLogoUrl:  string | null
  teamId:       string
  isEliminated: boolean
  isAvailable:  boolean
  injuryStatus: InjuryStatus
  totalPoints:  number
  gwPoints:     number | null
  avgPoints:    number | null
  nextOpponent: string | null
  gwHistory:    GwHistoryEntry[]
}

// Player DTO from GET /v1/leagues/{leagueId}/my-squad
export type MySquadPlayerDto = {
  playerId:     string
  name:         string
  displayName:  string
  position:     Position
  price:        number
  isStarter:    boolean
  positionSlot: number
  benchOrder:   number | null
  isEliminated: boolean
  teamCode:     string
  teamName:     string
  gwPoints:     number | null
}

export type SubstitutionEntry = {
  playerOutId:          string
  playerOutDisplayName: string
  playerInId:           string
  playerInDisplayName:  string
}

// Inputs for client-side transfer accounting preview.
// Omitted on historical snapshots. Empty `baselinePlayerIds` = pre-first-lock
// (unlimited free transfers apply).
export type TransferBaselineContext = {
  transferOption:              'OPTION_A' | 'OPTION_B'
  baselinePlayerIds:           string[]
  eliminatedBaselinePlayerIds: string[]
  byeBaselinePlayerIds:        string[]
  remainingEliminationCredits: number
  remainingByeCredits:         number
}

// Full GET /v1/leagues/{leagueId}/my-squad response
export type MySquadResult = {
  squadId:              string
  isLocked:             boolean
  totalFantasyPoints:   number   // always cumulative across all GWs
  totalTransfersUsed:   number
  budgetUsed:           number
  players:              MySquadPlayerDto[]
  isHistoricalSnapshot: boolean
  substitutions?:       SubstitutionEntry[]
  baselineContext?:     TransferBaselineContext
  gameweekNetPoints?:   number   // present only for historical (finalized) snapshots
}

// Full GET /v1/leagues/{leagueId}/teams/{userId} response
export type UserTeamResult = {
  squadId:              string
  userId:               string
  teamName:             string
  totalFantasyPoints:   number   // always cumulative across all GWs
  totalTransfersUsed:   number
  budgetUsed:           number
  players:              MySquadPlayerDto[]
  isHistoricalSnapshot: boolean
  gameweekId:           string | null
  gameweekNumber:       number | null
  substitutions?:       SubstitutionEntry[]
  gameweekNetPoints?:   number   // present only for historical (finalized) snapshots
}

// Result of the pure client-side accounting compute (mirrors backend TransferAccountingService.Accounting).
// `freeTransfersRemaining = -1` when the league uses Option B (unlimited).
export type AccountingSnapshot = {
  hasBaseline:                 boolean
  transferOption:              'OPTION_A' | 'OPTION_B'
  outgoingTotal:               number
  eliminationCreditsUsed:      number
  byeCreditsUsed:              number
  freeTransfersUsed:           number
  paidExtras:                  number
  penaltyPoints:               number   // ≤ 0
  freeTransfersRemaining:      number   // -1 for Option B
  eliminationCreditsRemaining: number
  byeCreditsRemaining:         number
}

export const FREE_TRANSFERS_PER_GW = 3
export const PENALTY_PER_EXTRA_TRANSFER = -5

// PUT /v1/squads/{squadId}/players request body
export type SaveSquadPayload = {
  starterPlayerIds: string[]   // exactly 11, order determines position slot assignment
  benchGkPlayerId:  string     // GK bench player ID
  benchPlayerIds:   string[]   // exactly 3, in sub priority order (Sub1 first)
}

// ── Player filter ─────────────────────────────────────────────────────────────

export type PlayerFilter = {
  search:         string
  position:       Position | 'ALL'
  teamCode:       string                 // '' = all teams
  sortBy:         'price' | 'points' | 'lastGw' | 'avgPoints' | 'name'
  sortDir:        'asc' | 'desc'
  hideEliminated: boolean
  minPrice:       number | null          // null = no lower bound (pool minimum)
  maxPrice:       number | null          // null = no upper bound (pool maximum)
  onBudgetOnly:   boolean                // when true, maxPrice is driven by remaining saldo
}

export const DEFAULT_PLAYER_FILTER: PlayerFilter = {
  search:         '',
  position:       'ALL',
  teamCode:       '',
  sortBy:         'points',              // default: top scorers first (overridden to 'price' before any GW is scored — see usePlayerSearch)
  sortDir:        'desc',
  hideEliminated: false,
  minPrice:       null,
  maxPrice:       null,
  onBudgetOnly:   false,
}

// ── Why a player cannot be added to the draft ─────────────────────────────────
export type AddBlockReason =
  | 'window_closed'
  | 'already_in_squad'
  | 'squad_full'
  | 'position_limit_reached'
  | 'budget_exceeded'
  | 'player_eliminated'
  | 'player_unavailable'

// ── Copy Squad — preview response (GET /v1/squads/{id}/copy-preview) ──────────
//
// Mirrors PreviewCopySquadResult on the backend. Staging-only — the FE drops
// the payload into the local draft (§13.10), then commits via the standard save flow.

export type CopyBlockerCode =
  | 'copy_window_closed'
  | 'copy_cross_competition'
  | 'copy_same_league'
  | 'source_squad_eliminated_players'
  | 'source_squad_over_budget'
  | 'source_squad_incomplete'
  | 'source_squad_not_found'
  | 'squad_not_found'
  | 'forbidden'

export type CopyBlocker = {
  errorCode: CopyBlockerCode | string
  playerIds?: string[]
  message?:   string
}

export type CopyStarter = {
  playerId:     string
  displayName:  string
  position:     Position
  price:        number
  positionSlot: number   // 1=GK, 2–6=DEF, 7–11=MID, 12–15=FWD
}

export type CopyPlayer = {
  playerId:    string
  displayName: string
  position:    Position
  price:       number
}

export type CopyBenchPlayer = {
  playerId:    string
  displayName: string
  position:    Position
  price:       number
  benchOrder:  number    // 1–3 (Sub1 → Sub3)
}

export type CopyPayload = {
  starters:      CopyStarter[]
  benchGk:       CopyPlayer
  benchOutfield: CopyBenchPlayer[]
}

export type TransferPenaltyPreview = {
  transferOption:              'OPTION_A' | 'OPTION_B'
  outgoingTotal:               number
  eliminationCreditsUsed:      number
  byeCreditsUsed:              number
  freeTransfersUsed:           number
  paidExtras:                  number
  penaltyPoints:               number   // ≤ 0
  freeTransfersRemaining:      number   // -1 for Option B
  eliminationCreditsRemaining: number
  byeCreditsRemaining:         number
}

export type PreviewCopySquadResult = {
  isEligible:      boolean
  blockers:        CopyBlocker[]
  payload?:        CopyPayload
  penaltyPreview?: TransferPenaltyPreview
}
